import React from "react";
import { motion } from "framer-motion";
import {
  FileText,
  UserRound,
  ClipboardList,
  ShieldCheck,
  Search,
  Stethoscope,
  CalendarCheck,
  Pill,
  Receipt,
  Database,
  LockKeyhole,
  ArrowRight,
  Check,
  Activity,
} from "lucide-react";
import Navbar from "../../../DashBoardComponents/Navbar";
import { useDispatch } from "react-redux";
import {
  handleLoginSignUpModal,
  LOGINMODAL,
} from "../../../Redux/actions/commonActions";

const EHR = () => {

  const dispatch = useDispatch();

  const handleExploreEHR = () => {
    dispatch(
      handleLoginSignUpModal({
        data: true,
        actionType: LOGINMODAL,
      })
    );
  };
  const ehrFeatures = [
    {
      icon: UserRound,
      title: "Digital Patient Records",
      description:
        "Maintain organised digital patient information and make relevant records easier to access during care.",
    },
    {
      icon: ClipboardList,
      title: "Clinical Documentation",
      description:
        "Document patient encounters, clinical notes and relevant healthcare information within a connected workflow.",
    },
    {
      icon: Search,
      title: "Quick Record Access",
      description:
        "Find and access relevant patient information when you need it during the healthcare workflow.",
    },
    {
      icon: Database,
      title: "Connected Records",
      description:
        "Connect digital records with supported appointments, prescriptions, billing and other healthcare workflows.",
    },
  ];

  const workflow = [
    {
      number: "01",
      icon: UserRound,
      title: "Create Patient Record",
      description:
        "Create and maintain a digital patient profile for the healthcare relationship.",
    },
    {
      number: "02",
      icon: ClipboardList,
      title: "Document Care",
      description:
        "Record relevant clinical information, notes and patient encounters.",
    },
    {
      number: "03",
      icon: Search,
      title: "Access Information",
      description:
        "Quickly find relevant patient information when providing care.",
    },
    {
      number: "04",
      icon: Activity,
      title: "Continue the Journey",
      description:
        "Connect supported records with other healthcare workflows across Doxzon.",
    },
  ];

  const connectedWorkflows = [
    {
      icon: CalendarCheck,
      title: "Scheduling",
      description:
        "Connect patient records with supported appointment and scheduling workflows.",
    },
    {
      icon: Stethoscope,
      title: "Virtual Care",
      description:
        "Support digital records as part of virtual healthcare workflows.",
    },
    {
      icon: Pill,
      title: "e-Prescriptions",
      description:
        "Connect supported prescription workflows with relevant patient information.",
    },
    {
      icon: Receipt,
      title: "Billing",
      description:
        "Connect supported billing information with the broader healthcare workflow.",
    },
  ];

  const benefits = [
    "Reduce dependence on disconnected paper records",
    "Keep patient information organised digitally",
    "Access relevant information during care",
    "Support consistent clinical documentation",
    "Connect records with other healthcare workflows",
    "Improve operational visibility across supported services",
  ];

  const faqs = [
    {
      question: "What is an EHR?",
      answer:
        "An Electronic Health Record, or EHR, is a digital system used to maintain and manage patient healthcare information and clinical records.",
    },
    {
      question: "Who is the EHR designed for?",
      answer:
        "The EHR experience is designed primarily for healthcare professionals and organisations that need to manage digital patient records and related workflows.",
    },
    {
      question: "What information can be stored in an EHR?",
      answer:
        "Depending on the implementation, an EHR may contain patient information, clinical documentation, encounter information, prescriptions and other relevant healthcare data.",
    },
    {
      question: "Can EHR connect with appointments?",
      answer:
        "Where supported, patient records can connect with appointment and scheduling workflows to provide a more integrated healthcare experience.",
    },
    {
      question: "Is patient information secure?",
      answer:
        "Healthcare organisations should use appropriate security, privacy and access-control measures when managing patient information. The exact controls depend on the Doxzon implementation and applicable requirements.",
    },
  ];

  return (
    <>
      <Navbar />

      <main className="bg-white text-slate-900 overflow-hidden">

        {/* =====================================================
            HERO
        ====================================================== */}
        <section className="relative overflow-hidden bg-[#f4fbfc]">

          <div className="absolute inset-0 pointer-events-none">

            <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-teal-200/25 blur-3xl" />

            <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-cyan-200/25 blur-3xl" />

            <div
              className="absolute inset-0 opacity-[0.025]"
              style={{
                backgroundImage:
                  "linear-gradient(#0f172a 1px, transparent 1px), linear-gradient(90deg, #0f172a 1px, transparent 1px)",
                backgroundSize: "48px 48px",
              }}
            />

          </div>

          <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-16 items-center">

            {/* LEFT */}
            <motion.div
              initial={{ opacity: 0, x: -35 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >

              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-teal-100 px-4 py-2 shadow-sm">

                <span className="w-2 h-2 rounded-full bg-teal-500" />

                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700">
                  EHR / Digital Records
                </span>

              </div>

              <h1 className="font-sora text-5xl md:text-6xl lg:text-[62px] font-medium tracking-[-0.055em] leading-[1.03] mt-7">

                Patient records,
                <span className="block text-teal-500 mt-2">
                  built for connected care.
                </span>

              </h1>

              <p className="text-slate-600 text-lg md:text-xl leading-8 mt-7 max-w-xl">
                Manage digital patient records, clinical documentation and
                connected healthcare workflows through one organised EHR
                experience.
              </p>

              {/* HERO ACTION */}
              <div className="mt-9 rounded-2xl bg-white border border-slate-100 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.10)]">

                <div className="flex items-center gap-4">

                  <div className="w-12 h-12 rounded-xl bg-teal-50 grid place-items-center">

                    <FileText
                      size={22}
                      className="text-teal-600"
                    />

                  </div>

                  <div className="flex-1">

                    <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400">
                      Digital Health Records
                    </p>

                    <p className="font-semibold mt-1">
                      Manage patient information in one place
                    </p>

                  </div>

                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={handleExploreEHR}
                    className="hidden sm:inline-flex items-center gap-2 rounded-xl bg-slate-950 text-white px-5 h-12 text-sm font-semibold"
                  >
                    Explore EHR
                    <ArrowRight size={15} />
                  </motion.button>

                </div>

                <button
  onClick={handleExploreEHR}
  className="sm:hidden w-full mt-4 h-12 rounded-xl bg-slate-950 text-white font-semibold text-sm"
>
  Explore EHR
</button>

              </div>

              <div className="flex flex-wrap gap-6 mt-7 text-sm text-slate-500">

                <span className="inline-flex items-center gap-2">
                  <ShieldCheck size={16} className="text-teal-500" />
                  Privacy-conscious
                </span>

                <span className="inline-flex items-center gap-2">
                  <Database size={16} className="text-teal-500" />
                  Digital records
                </span>

              </div>

            </motion.div>

            {/* RIGHT EHR CARD */}
            <motion.div
              initial={{ opacity: 0, x: 35, scale: 0.96 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="relative"
            >

              <div className="absolute -inset-8 rounded-[45px] bg-teal-200/30 blur-3xl" />

              <div className="relative bg-white rounded-[30px] border border-slate-100 shadow-[0_30px_80px_rgba(15,23,42,0.14)] p-6 md:p-7">

                <div className="flex items-center justify-between pb-5 border-b border-slate-100">

                  <div>

                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
                      EHR Workspace
                    </p>

                    <h3 className="font-sora text-xl font-semibold mt-1">
                      Patient Record
                    </h3>

                  </div>

                  <div className="w-11 h-11 rounded-xl bg-teal-50 grid place-items-center">

                    <FileText
                      size={21}
                      className="text-teal-600"
                    />

                  </div>

                </div>

                {/* PATIENT */}
                <div className="mt-6 rounded-2xl bg-slate-50 border border-slate-100 p-5">

                  <div className="flex items-center gap-4">

                    <div className="w-12 h-12 rounded-xl bg-teal-100 grid place-items-center">

                      <UserRound
                        size={22}
                        className="text-teal-700"
                      />

                    </div>

                    <div className="flex-1">

                      <p className="font-semibold text-sm">
                        Patient Profile
                      </p>

                      <p className="text-xs text-slate-400 mt-1">
                        Digital health record
                      </p>

                    </div>

                    <span className="text-xs font-semibold text-teal-600">
                      Active
                    </span>

                  </div>

                </div>

                {/* RECORD ITEMS */}
                <div className="mt-5">

                  <div className="flex items-center justify-between mb-3">

                    <p className="text-sm font-semibold">
                      Record Information
                    </p>

                    <Search
                      size={17}
                      className="text-teal-600"
                    />

                  </div>

                  <div className="space-y-2">

                    {[
                      ["Clinical Notes", ClipboardList],
                      ["Appointments", CalendarCheck],
                      ["Prescriptions", Pill],
                      ["Billing Information", Receipt],
                    ].map(([title, Icon]) => (

                      <div
                        key={title}
                        className="flex items-center gap-3 rounded-xl bg-slate-50 p-3"
                      >

                        <div className="w-9 h-9 rounded-lg bg-white grid place-items-center">

                          <Icon
                            size={16}
                            className="text-teal-600"
                          />

                        </div>

                        <span className="text-xs font-semibold flex-1">
                          {title}
                        </span>

                        <Check
                          size={15}
                          className="text-teal-500"
                        />

                      </div>

                    ))}

                  </div>

                </div>

                {/* SECURITY */}
                <div className="mt-5 rounded-2xl bg-slate-950 text-white p-5">

                  <div className="flex items-center gap-3">

                    <div className="w-9 h-9 rounded-xl bg-teal-500/10 grid place-items-center">

                      <LockKeyhole
                        size={17}
                        className="text-teal-400"
                      />

                    </div>

                    <div>

                      <p className="text-xs font-semibold">
                        Protected Information
                      </p>

                      <p className="text-[10px] text-slate-400 mt-1">
                        Access based on supported permissions
                      </p>

                    </div>

                  </div>

                </div>

              </div>

            </motion.div>

          </div>

        </section>

        {/* =====================================================
            FEATURES
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-7xl mx-auto px-6">

            <div className="max-w-2xl">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                EHR Capabilities
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Everything around the
                <span className="text-teal-500">
                  {" "}patient record.
                </span>

              </h2>

              <p className="text-slate-600 leading-7 mt-5">
                Keep patient information and clinical workflows organised
                through a connected digital records experience.
              </p>

            </div>

            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-8">

  {ehrFeatures.map((item, index) => {

    const Icon = item.icon;

    return (
      <motion.div
        key={item.title}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{
          duration: 0.5,
          delay: index * 0.06,
        }}
        whileHover={{ y: -4 }}
        className="group rounded-xl border border-slate-100 bg-slate-50/70 p-4 hover:bg-white hover:shadow-md hover:border-teal-100 transition"
      >

        <div className="w-9 h-9 rounded-lg bg-teal-50 grid place-items-center group-hover:bg-teal-500 transition">
          <Icon
            size={17}
            className="text-teal-600 group-hover:text-white transition"
          />
        </div>

        <h3 className="font-sora text-sm font-semibold mt-3">
          {item.title}
        </h3>

        <p className="text-slate-500 text-[11px] leading-5 mt-1.5">
          {item.description}
        </p>

      </motion.div>
    );
  })}

</div>

          </div>

        </section>

        {/* =====================================================
            WORKFLOW
        ====================================================== */}
        <section className="py-24 md:py-28 bg-[#f6fbfc]">

          <div className="max-w-6xl mx-auto px-6">

            <div className="text-center max-w-2xl mx-auto">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Provider Workflow
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Patient records made
                <span className="text-teal-500">
                  {" "}easier to manage.
                </span>

              </h2>

            </div>

            <div className="relative grid md:grid-cols-4 gap-6 mt-14">

              <div className="hidden md:block absolute top-7 left-[11%] right-[11%] h-px bg-teal-100" />

              {workflow.map((item) => {

                const Icon = item.icon;

                return (
                  <motion.div
                    key={item.number}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="relative text-center"
                  >

                    <div className="relative z-10 mx-auto w-14 h-14 rounded-2xl bg-slate-950 border-4 border-[#f6fbfc] shadow-lg grid place-items-center">

                      <Icon
                        size={21}
                        className="text-teal-400"
                      />

                    </div>

                    <span className="block mt-5 text-[10px] font-bold tracking-[0.2em] text-teal-600">
                      STEP {item.number}
                    </span>

                    <h3 className="font-sora text-lg font-semibold mt-2">
                      {item.title}
                    </h3>

                    <p className="text-slate-500 text-sm leading-6 mt-2 max-w-[230px] mx-auto">
                      {item.description}
                    </p>

                  </motion.div>

                );

              })}

            </div>

          </div>

        </section>

        {/* =====================================================
            CONNECTED WORKFLOWS
        ====================================================== */}
        <section className="py-24 md:py-28 bg-slate-950 text-white">

          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">

            <div>

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-400">
                Connected Platform
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                More than a record.
                <span className="block text-teal-400">
                  A connected workflow.
                </span>

              </h2>

              <p className="text-slate-400 text-lg leading-8 mt-6 max-w-xl">
                EHR and digital records can become the foundation connecting
                multiple healthcare workflows across the Doxzon platform.
              </p>

            </div>

            <div className="space-y-3">

              {connectedWorkflows.map((item, index) => {

                const Icon = item.icon;

                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.07 }}
                    whileHover={{ x: 5 }}
                    className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4"
                  >

                    <div className="w-10 h-10 rounded-xl bg-teal-500/10 grid place-items-center">

                      <Icon
                        size={18}
                        className="text-teal-400"
                      />

                    </div>

                    <div className="flex-1">

                      <p className="text-sm font-semibold">
                        {item.title}
                      </p>

                      <p className="text-xs text-slate-500 mt-1">
                        {item.description}
                      </p>

                    </div>

                    <ArrowRight
                      size={16}
                      className="text-slate-500"
                    />

                  </motion.div>
                );

              })}

            </div>

          </div>

        </section>

        {/* =====================================================
            SECURITY
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-6xl mx-auto px-6">

            <div className="rounded-[32px] bg-[#f6fbfc] border border-teal-50 p-8 md:p-12 grid lg:grid-cols-2 gap-12 items-center">

              <div>

                <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                  Security & Privacy
                </span>

                <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                  Patient information
                  <span className="block text-teal-500">
                    deserves protection.
                  </span>

                </h2>

                <p className="text-slate-600 leading-8 mt-6">
                  Digital healthcare records require appropriate privacy,
                  security and access controls. Your exact compliance and
                  security requirements depend on the implementation and
                  applicable regulations.
                </p>

              </div>

              <div className="grid grid-cols-2 sm:grid-cols-2 gap-4">

                {[
                  [ShieldCheck, "Access Controls"],
                  [LockKeyhole, "Data Protection"],
                  [Database, "Digital Records"],
                  [FileText, "Audit Information"],
                ].map(([Icon, title], index) => (

                  <motion.div
                    key={title}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.06 }}
                    className="rounded-2xl bg-white border border-slate-100 p-5"
                  >

                    <div className="w-10 h-10 rounded-xl bg-teal-50 grid place-items-center">

                      <Icon
                        size={18}
                        className="text-teal-600"
                      />

                    </div>

                    <p className="font-semibold text-sm mt-4">
                      {title}
                    </p>

                  </motion.div>

                ))}

              </div>

            </div>

          </div>

        </section>

        {/* =====================================================
            BENEFITS
        ====================================================== */}
        <section className="py-24 md:py-28 bg-gradient-to-br from-cyan-50 to-blue-50">

          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">

            <div>

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Provider Benefits
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Spend less time
                <span className="block text-teal-500">
                  searching for information.
                </span>

              </h2>

              <p className="text-slate-600 leading-8 mt-6 max-w-xl">
                Give healthcare professionals a more organised way to work
                with patient information and connected healthcare workflows.
              </p>

            </div>

            <div className="space-y-3">

              {benefits.map((item, index) => (

                <motion.div
                  key={item}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ x: 5 }}
                  className="flex items-center gap-4 rounded-2xl border border-white bg-white/80 p-4 shadow-sm"
                >

                  <span className="w-8 h-8 rounded-full bg-teal-50 text-teal-600 grid place-items-center shrink-0">

                    <Check size={15} />

                  </span>

                  <span className="text-sm md:text-base font-medium text-slate-700">
                    {item}
                  </span>

                </motion.div>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            FAQ
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-4xl mx-auto px-6">

            <div className="text-center">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                FAQ
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">
                Frequently asked questions
              </h2>

            </div>

            <div className="space-y-3 mt-12">

              {faqs.map((faq, index) => (

                <motion.details
                  key={faq.question}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="group rounded-2xl border border-slate-100 bg-slate-50 open:bg-white open:shadow-sm"
                >

                  <summary className="cursor-pointer list-none flex items-center justify-between gap-5 p-6 font-sora font-semibold">

                    <span>
                      {faq.question}
                    </span>

                    <span className="w-8 h-8 rounded-full bg-white group-open:bg-teal-500 group-open:text-white grid place-items-center shrink-0 transition">

                      <ArrowRight
                        size={15}
                        className="group-open:rotate-90 transition-transform"
                      />

                    </span>

                  </summary>

                  <p className="px-6 pb-6 text-slate-600 leading-7 text-sm md:text-base">
                    {faq.answer}
                  </p>

                </motion.details>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            FINAL CTA
        ====================================================== */}
        <section className="px-6 pb-24">

          <div className="relative overflow-hidden max-w-7xl mx-auto rounded-[32px] bg-slate-950 text-white px-8 py-16 md:px-16 text-center">

            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-teal-500/15 blur-3xl" />

            <div className="absolute -bottom-28 -left-20 w-72 h-72 rounded-full bg-cyan-500/10 blur-3xl" />

            <div className="relative">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-400">
                Healthcare Professionals
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Build a more connected
                <span className="text-teal-400">
                  {" "}patient record workflow.
                </span>

              </h2>

              <p className="text-slate-300 max-w-2xl mx-auto mt-5 leading-7">
                Bring digital patient records, clinical documentation and
                supported healthcare workflows together with Doxzon.
              </p>

              <button
  onClick={handleExploreEHR}
  className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-slate-900 px-7 py-4 font-semibold hover:bg-teal-50 transition"
>
  Explore EHR
  <ArrowRight size={17} />
</button>

            </div>

          </div>

        </section>

      </main>
    </>
  );
};

export default EHR;